import React from 'react';
import { TrendingUpIcon, TrendingDownIcon, TargetIcon, FlameIcon, TrophyIcon, ZapIcon, BarChart3Icon } from 'lucide-react';
import { PeriodData, Timeframe, getPacingStatus, getPacingColor, getProjectedTotal, getAchievementStatus } from '../utils/goalData';
interface SidebarProps {
  periodData: PeriodData;
  timeframe: Timeframe;
}
export function Sidebar({
  periodData,
  timeframe
}: SidebarProps) {
  const spExpected = periodData.totalDays > 0 ? periodData.softPull.goal / periodData.totalDays * periodData.daysPassed : 0;
  const subExpected = periodData.totalDays > 0 ? periodData.submitted.goal / periodData.totalDays * periodData.daysPassed : 0;
  const spStatus = getPacingStatus(periodData.softPull.current, spExpected);
  const subStatus = getPacingStatus(periodData.submitted.current, subExpected);
  const spColors = getPacingColor(spStatus);
  const subColors = getPacingColor(subStatus);
  const spProjected = getProjectedTotal(periodData.softPull.current, periodData.daysPassed, periodData.totalDays);
  const subProjected = getProjectedTotal(periodData.submitted.current, periodData.daysPassed, periodData.totalDays);
  // Calculate streak from historical data
  const historicalAchievements = periodData.historicalPeriods.map(p => {
    const spPct = p.softPullGoal > 0 ? p.softPullActual / p.softPullGoal : 0;
    const subPct = p.submittedGoal > 0 ? p.submittedActual / p.submittedGoal : 0;
    return Math.round((spPct + subPct) / 2 * 100);
  });
  let streak = 0;
  for (const pct of historicalAchievements) {
    if (pct >= 100) {
      streak++;
    } else {
      break;
    }
  }
  const avgAchievement = Math.round(historicalAchievements.reduce((a, b) => a + b, 0) / historicalAchievements.length);
  const overallPct = Math.round((periodData.softPull.current / periodData.softPull.goal + periodData.submitted.current / periodData.submitted.goal) / 2 * 100);
  const elapsedPct = Math.round(periodData.daysPassed / periodData.totalDays * 100);
  return <aside className="w-72 flex-shrink-0 hidden lg:block" aria-label="Goal summary sidebar" data-id="element-164">
      <div className="sticky top-20 space-y-4" data-id="element-165">
        {/* Overall Progress */}
        <div className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-5" style={{
        boxShadow: '0 0 25px rgba(99, 102, 241, 0.08)'
      }} data-id="element-166">
          <div className="flex items-center gap-2 mb-4" data-id="element-167">
            <BarChart3Icon className="w-4 h-4 text-indigo-400" data-id="element-168" />
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider" data-id="element-169">
              Overview
            </h3>
          </div>

          <div className="text-center mb-4" data-id="element-170">
            <span className="text-4xl font-bold text-white" data-id="element-171">{overallPct}%</span>
            <p className="text-xs text-slate-500 mt-1" data-id="element-172">Overall Completion</p>
          </div>

          <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-2" data-id="element-173">
            <div className="h-full rounded-full transition-all duration-700" style={{
            width: `${Math.min(overallPct, 100)}%`,
            background: 'linear-gradient(90deg, #818cf8, #a78bfa)',
            boxShadow: '0 0 8px rgba(129, 140, 248, 0.4)'
          }} data-id="element-174" />
          </div>
          <div className="flex justify-between text-[10px] text-slate-500" data-id="element-175">
            <span data-id="element-176">Day {periodData.daysPassed}</span>
            <span data-id="element-177">{elapsedPct}% elapsed</span>
          </div>
        </div>

        {/* Metric Summaries */}
        <div className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-5 space-y-4" style={{
        boxShadow: '0 0 25px rgba(99, 102, 241, 0.06)'
      }} data-id="element-178">
          <div className="flex items-center gap-2 mb-1" data-id="element-179">
            <TargetIcon className="w-4 h-4 text-indigo-400" data-id="element-180" />
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider" data-id="element-181">
              Metrics
            </h3>
          </div>

          {/* Soft Pull */}
          <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30" data-id="element-182">
            <div className="flex items-center justify-between mb-2" data-id="element-183">
              <span className="text-xs font-medium text-slate-400" data-id="element-184">
                Soft Pull
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ${spColors.bg} ${spColors.text} ${spColors.ring}`} data-id="element-185">
                {spStatus}
              </span>
            </div>
            <div className="flex items-baseline gap-1 mb-1.5" data-id="element-186">
              <span className="text-xl font-bold text-indigo-400" data-id="element-187">
                {periodData.softPull.current}
              </span>
              <span className="text-xs text-slate-500" data-id="element-188">
                / {periodData.softPull.goal}
              </span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden" data-id="element-189">
              <div className="h-full rounded-full" style={{
              width: `${Math.min(periodData.softPull.current / periodData.softPull.goal * 100, 100)}%`,
              background: 'linear-gradient(90deg, #6366f1, #8b5cf6)'
            }} data-id="element-190" />
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5" data-id="element-191">
              Projected:{' '}
              <span className={`font-semibold ${spProjected >= periodData.softPull.goal ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-192">
                {spProjected}
              </span>
            </p>
          </div>

          {/* Submitted */}
          <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30" data-id="element-193">
            <div className="flex items-center justify-between mb-2" data-id="element-194">
              <span className="text-xs font-medium text-slate-400" data-id="element-195">
                Submitted
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ${subColors.bg} ${subColors.text} ${subColors.ring}`} data-id="element-196">
                {subStatus}
              </span>
            </div>
            <div className="flex items-baseline gap-1 mb-1.5" data-id="element-197">
              <span className="text-xl font-bold text-cyan-400" data-id="element-198">
                {periodData.submitted.current}
              </span>
              <span className="text-xs text-slate-500" data-id="element-199">
                / {periodData.submitted.goal}
              </span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden" data-id="element-200">
              <div className="h-full rounded-full" style={{
              width: `${Math.min(periodData.submitted.current / periodData.submitted.goal * 100, 100)}%`,
              background: 'linear-gradient(90deg, #06b6d4, #3b82f6)'
            }} data-id="element-201" />
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5" data-id="element-202">
              Projected:{' '}
              <span className={`font-semibold ${subProjected >= periodData.submitted.goal ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-203">
                {subProjected}
              </span>
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-5 space-y-3" style={{
        boxShadow: '0 0 25px rgba(99, 102, 241, 0.06)'
      }} data-id="element-204">
          <div className="flex items-center gap-2 mb-1" data-id="element-205">
            <TrophyIcon className="w-4 h-4 text-amber-400" data-id="element-206" />
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider" data-id="element-207">
              Stats
            </h3>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-700/30" data-id="element-208">
            <div className="flex items-center gap-2" data-id="element-209">
              <FlameIcon className="w-4 h-4 text-orange-400" data-id="element-210" />
              <span className="text-sm text-slate-400" data-id="element-211">Streak</span>
            </div>
            <span className="text-sm font-bold text-white" data-id="element-212">
              {streak} {timeframe === 'weekly' ? 'wk' : 'mo'}
            </span>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-700/30" data-id="element-213">
            <div className="flex items-center gap-2" data-id="element-214">
              {avgAchievement >= 100 ? <TrendingUpIcon className="w-4 h-4 text-emerald-400" data-id="element-215" /> : <TrendingDownIcon className="w-4 h-4 text-amber-400" data-id="element-216" />}
              <span className="text-sm text-slate-400" data-id="element-217">Avg Rate</span>
            </div>
            <span className={`text-sm font-bold ${avgAchievement >= 100 ? 'text-emerald-400' : 'text-slate-200'}`} data-id="element-218">
              {avgAchievement}%
            </span>
          </div>

          <div className="flex items-center justify-between py-2" data-id="element-219">
            <div className="flex items-center gap-2" data-id="element-220">
              <ZapIcon className="w-4 h-4 text-amber-400" data-id="element-221" />
              <span className="text-sm text-slate-400" data-id="element-222">Days Left</span>
            </div>
            <span className="text-sm font-bold text-white" data-id="element-223">
              {periodData.daysRemaining}
            </span>
          </div>
        </div>
      </div>
    </aside>;
}